import { EntityKind } from 'mdello-client-ts/dist/src/interfaces/admin/entity.interface';
import { Profile } from 'mdello-client-ts/dist/src/interfaces/providers/ecommerce/profile.interface';
import { useQuery } from 'react-query';
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum';
import { getDataFromLocalStorage } from 'src/localStorage/getDataFromLocalStorage';
import { useNotification } from 'src/providers/notificationsProvider';
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum';
import { fetchCustomers } from 'src/services/napi/customers/fetchCustomers/index';
import { Customer } from 'src/services/napi/customers/interfaces/fetchCustomers.interface';
import { Project } from 'src/services/napi/projects/interfaces/fetchProjects.interface';
import { getClient } from 'src/util/mdello-instance';

export interface CompanyUserMetadata {
  accountType?: string;
  companyId?: string;
  companyName?: string;
}

export interface CompanyUser extends Omit<Customer, 'metadata'> {
  created?: string;
  metadata?: CompanyUserMetadata;
}

export interface CompanyRow {
  profile?: Profile;
  user: CompanyUser;
}

export interface CompaniesResponseData {
  data: CompanyRow[];
  paging: { total: number };
}

const fetchCompanies = async (): Promise<CompaniesResponseData | undefined> => {
  const currentProject = getDataFromLocalStorage<Project>(LocalStorageKey.CURRENT_PROJECT);

  if (!currentProject?._id) return;

  const [customers, profiles] = await Promise.all([
    fetchCustomers(currentProject._id),
    getClient()?.listEntities({
      kind: EntityKind.PROFILE,
      projectId: currentProject._id
    })
  ]);

  const profileMap = new Map<string, Profile>();
  (profiles?.data ?? []).forEach((profile: Profile) => {
    profileMap.set(String(profile.userId), profile);
  });

  const rows: CompanyRow[] = ((customers?.users ?? []) as CompanyUser[])
    .filter((user) => {
      const profile = profileMap.get(String(user._id));
      return user.metadata?.accountType === 'company' || profile?.type === 'COMPANY';
    })
    .map((user) => ({
      profile: profileMap.get(String(user._id)),
      user
    }));

  return { data: rows, paging: { total: rows.length } };
};

export const useGetCompanies = () => {
  const { notify } = useNotification();

  return useQuery([QueryKey.COMPANIES], () => fetchCompanies(), {
    keepPreviousData: true,
    onError: () => {
      notify({ message: 'Error Loading data... Try again later', severity: 'error' });
    }
  });
};
